import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  FormControlLabel,
  RadioGroup,
  Radio,
  Button,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import api from '../services/api';
import { isIncompleteFalsePositive, FP_REASON_REQUIRED, getPresentReviewStats } from '../utils/reviewClassification';

/**
 * The single review-submit system for evidence-preview modals — verdict
 * chips (True Anomaly / False Positive), the False Positive reason box,
 * Generic/Retailer-specific scope, and the Submit Review button that writes
 * a `review_signals` row. Used identically by both `EvidenceReviewModal`
 * instances in `AnomaliesTab.js` (issueType="null-issues" and
 * "non-null-issues") so the two can never drift again — see
 * `docs/prds/2026-09-17-review-signal-issue-type.md`.
 *
 * Renders the full panel — chips, reason box, scope, and Submit Review — for
 * every `page_type` and every `issue_type`; the backend's
 * `submit_review_signal` (`app/api/routers/anomalies.py`) accepts a review
 * signal for any anomaly with an occurrence + evidence_index to key on.
 * The narrower "what rule-synthesis actually uses" restriction
 * (page_type == 'detail' AND issue_type == 'null-issues') is enforced on the
 * consumer side, in `ReviewSignalRepository.findPendingFalsePositives`'s
 * query in `rule-synthesis-service` — see
 * `docs/seams/anomaly-feedback-app-to-rule-synthesis-service.md`. This
 * component does not need to know about that restriction at all.
 */
function ReviewSubmitPanel({
  // Hides the panel entirely — mirrors each modal's own "can this even be
  // classified" check (`onUpdateVerdict` / `onPersistClassifications` being
  // supplied at all).
  editable = true,
  // Informational only -- not used to gate anything in this component.
  // Passed through by callers for parity with the modal's other props.
  pageType,
  // Current sample's attribute presence — 'absent' samples are auto-skipped
  // and never need a verdict.
  presence,
  anomalyId,
  // Already-resolved "current" occurrence id (latest, or explicitly picked).
  occurrenceId,
  // REAL index into the full evidence array — never a filtered display
  // position. This is what's sent to the API and what keys the
  // per-sample "submitted" state.
  evidenceIndex,
  // {verdict, fpReason, scope_type} for the sample at evidenceIndex.
  classification,
  // Full classifications array + full evidence list, for the header stats.
  classifications,
  evidenceList,
  onVerdictChange,
  onFpReasonChange,
  onScopeTypeChange,
}) {
  const [submittingIndex, setSubmittingIndex] = useState(null);
  const [signalError, setSignalError] = useState('');
  // Map of "occurrenceId:index" -> the {verdict, fpReason, scope_type}
  // snapshot last successfully submitted, so "Submitted" only shows while
  // the content still matches what's on the server (editing after submit
  // reverts to "Submit Review", allowing an explicit resubmit).
  const [submittedSignals, setSubmittedSignals] = useState(() => new Map());

  // Clear a stale submit error when the user moves to a different sample or
  // occurrence — the "Submitted" state itself persists per (occurrence,
  // index) key, so revisiting an already-submitted sample still shows it.
  useEffect(() => {
    setSignalError('');
  }, [evidenceIndex, occurrenceId]);

  const cls = classification || {};
  const currentNeedsFpReason = isIncompleteFalsePositive(cls);
  const signalKey = occurrenceId != null ? `${occurrenceId}:${evidenceIndex}` : null;
  const submittedSnapshot = signalKey ? submittedSignals.get(signalKey) : null;
  const currentSignalSubmitted = !!submittedSnapshot
    && submittedSnapshot.verdict === cls.verdict
    && submittedSnapshot.fpReason === (cls.fpReason || '')
    && submittedSnapshot.scope_type === (cls.scope_type || 'retailer_specific');

  const handleSubmitReviewSignal = useCallback(async () => {
    const verdict = cls.verdict;
    if (verdict !== 'true_anomaly' && verdict !== 'false_positive') return;
    if (verdict === 'false_positive' && !cls.fpReason?.trim()) return;
    if (!anomalyId || !occurrenceId) {
      setSignalError('No occurrence available for this anomaly — cannot submit.');
      return;
    }

    const fpReason = cls.fpReason || '';
    const scopeType = cls.scope_type || 'retailer_specific';
    setSignalError('');
    setSubmittingIndex(evidenceIndex);
    try {
      await api.submitReviewSignal(anomalyId, {
        occurrenceId,
        evidenceIndex,
        verdict,
        fpReason,
        scopeType,
      });
      setSubmittedSignals((prev) => {
        const next = new Map(prev);
        next.set(`${occurrenceId}:${evidenceIndex}`, { verdict, fpReason, scope_type: scopeType });
        return next;
      });
    } catch (err) {
      setSignalError(err?.message || 'Failed to submit review signal');
    } finally {
      setSubmittingIndex(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anomalyId, occurrenceId, evidenceIndex, cls.verdict, cls.fpReason, cls.scope_type]);

  if (!editable) return null;
  if ((presence || '').toLowerCase() === 'absent') return null;
  if (!classifications || classifications.length === 0) return null;

  const { trueCount, fpCount, pending } = getPresentReviewStats(classifications, evidenceList);
  const dot = { display: 'inline-block', width: 6, height: 6, borderRadius: '50%', mr: 0.5 };

  return (
    <Box sx={{
      flexShrink: 0,
      maxHeight: '48%',
      overflow: 'auto',
      p: 1.25,
      borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
      background: 'rgba(255, 255, 255, 0.02)',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
        <Typography variant="caption" sx={{ color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 600 }}>
          Classification
        </Typography>
        <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
            <Box component="span" sx={{ ...dot, background: '#10b981' }} />{trueCount} true
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
            <Box component="span" sx={{ ...dot, background: '#ef4444' }} />{fpCount} fp
          </Typography>
          <Typography sx={{ fontSize: '0.7rem', color: '#94a3b8' }}>
            <Box component="span" sx={{ ...dot, background: '#64748b' }} />{pending} pending
          </Typography>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        {[
          { value: 'true_anomaly', label: 'True Anomaly', color: '#10b981' },
          { value: 'false_positive', label: 'False Positive', color: '#ef4444' },
        ].map((opt) => {
          const selected = cls.verdict === opt.value;
          return (
            <Box
              key={opt.value}
              onClick={() => onVerdictChange(opt.value)}
              sx={{
                flex: 1,
                cursor: 'pointer',
                textAlign: 'center',
                py: 0.75,
                borderRadius: '6px',
                border: `1px solid ${selected ? opt.color : 'rgba(255,255,255,0.12)'}`,
                background: selected ? `${opt.color}1a` : 'transparent',
                color: selected ? opt.color : '#94a3b8',
                fontSize: '0.82rem',
                fontWeight: 600,
                transition: 'all 0.15s',
                '&:hover': { borderColor: opt.color, color: opt.color },
              }}
            >
              {opt.label}
            </Box>
          );
        })}
      </Box>

      {cls.verdict === 'false_positive' && (
        <>
          <TextField
            fullWidth
            required
            multiline
            minRows={2}
            maxRows={4}
            size="small"
            placeholder="Why is this a false positive? (required)"
            value={cls.fpReason || ''}
            onChange={(e) => onFpReasonChange(e.target.value)}
            error={currentNeedsFpReason}
            helperText={currentNeedsFpReason ? FP_REASON_REQUIRED : ''}
            sx={{
              mt: 1,
              '& .MuiInputBase-root': { fontSize: '0.82rem', color: '#e2e8f0', background: 'rgba(15,23,42,0.5)', borderRadius: '6px' },
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: currentNeedsFpReason
                  ? 'rgba(239,68,68,0.7)' : 'rgba(100,116,139,0.3)',
              },
              '& .MuiFormHelperText-root': { color: '#ef4444', mx: 0.5 },
            }}
          />
          <Box sx={{ mt: 1, p: 0.75, background: '#1e293b', borderRadius: '6px', border: '1px solid #475569', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
            <Typography variant="caption" sx={{ color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.5px', fontWeight: 700, fontSize: '0.68rem', whiteSpace: 'nowrap' }}>
              📋 Scope
            </Typography>
            <RadioGroup
              row
              value={cls.scope_type || 'retailer_specific'}
              onChange={(e) => onScopeTypeChange(e.target.value)}
              sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
            >
              <FormControlLabel
                value="generic"
                control={<Radio size="small" sx={{ p: 0.5, color: '#94a3b8', '&.Mui-checked': { color: '#a78bfa' } }} />}
                label={<Typography sx={{ fontSize: '0.78rem', color: '#e2e8f0', fontWeight: 500 }}>Generic (all retailers)</Typography>}
                sx={{ margin: 0, mr: 1 }}
              />
              <FormControlLabel
                value="retailer_specific"
                control={<Radio size="small" sx={{ p: 0.5, color: '#94a3b8', '&.Mui-checked': { color: '#a78bfa' } }} />}
                label={<Typography sx={{ fontSize: '0.78rem', color: '#e2e8f0', fontWeight: 500 }}>Retailer-specific</Typography>}
                sx={{ margin: 0 }}
              />
            </RadioGroup>
          </Box>
          {(() => {
            const reasonOk = !!cls.fpReason?.trim();
            const isSubmitting = submittingIndex === evidenceIndex;
            const disabled = !reasonOk || !occurrenceId || isSubmitting;
            return (
              <Box sx={{ mt: 1.25 }}>
                <Button
                  fullWidth
                  disableElevation
                  onClick={disabled ? undefined : handleSubmitReviewSignal}
                  disabled={disabled}
                  variant={currentSignalSubmitted ? 'outlined' : 'contained'}
                  color={currentSignalSubmitted ? 'success' : 'primary'}
                  startIcon={
                    isSubmitting ? (
                      <CircularProgress size={14} sx={{ color: 'inherit' }} />
                    ) : currentSignalSubmitted ? (
                      <CheckCircleIcon sx={{ fontSize: '1rem' }} />
                    ) : null
                  }
                  sx={{
                    textTransform: 'none',
                    fontSize: '0.82rem',
                    fontWeight: 600,
                    borderRadius: '6px',
                    py: 0.85,
                    ...(currentSignalSubmitted
                      ? { color: '#10b981', borderColor: 'rgba(16,185,129,0.5)' }
                      : {}),
                  }}
                >
                  {isSubmitting ? 'Submitting…' : currentSignalSubmitted ? 'Submitted' : 'Submit Review'}
                </Button>
                {!occurrenceId && (
                  <Typography sx={{ mt: 0.5, fontSize: '0.7rem', color: '#f59e0b' }}>
                    No occurrence context available for this anomaly — cannot submit yet.
                  </Typography>
                )}
                {occurrenceId && !reasonOk && (
                  <Typography sx={{ mt: 0.5, fontSize: '0.7rem', color: '#ef4444' }}>
                    {FP_REASON_REQUIRED}
                  </Typography>
                )}
                {signalError && (
                  <Typography sx={{ mt: 0.5, fontSize: '0.7rem', color: '#ef4444' }}>
                    {signalError}
                  </Typography>
                )}
              </Box>
            );
          })()}
        </>
      )}
    </Box>
  );
}

export default ReviewSubmitPanel;
